import { Button, Table } from '@dev-spendesk/grapes';
import { isValid } from 'date-fns';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import type { Payable } from 'modules/payable/models';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { payableTableColumns } from './PayableTableColumns';
import { PayableTableGroup } from './PayableTableGroup';
import { PayableTableSkeleton } from './PayableTableSkeleton';

type PayableTableProps = {
  payables: Payable[];
  totalPayables: number;
  hasNextPage?: boolean;
  activePayableId: string | undefined;
  onFetchNextPage?(): void;
  onClick(payableId: string): void;
  renderActions?: (params: {
    numberOfSelectedRows: number;
    areAllRowsSelected: boolean;
    unselectedRows: string[];
    selectedRows: string[];
    setSelectedRows: (rows: string[]) => void;
  }) => ReactNode;
  isLoading?: boolean;
};

export const PayableTable = ({
  payables,
  onClick,
  activePayableId,
  totalPayables,
  hasNextPage,
  onFetchNextPage,
  renderActions,
  isLoading,
}: PayableTableProps) => {
  const { t } = useTranslation('global');

  const columns = payableTableColumns(t);

  const data = useMemo(
    () => payables.filter((payable) => payable !== undefined),
    [payables],
  );

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [areAllRowsSelected, setAreAllRowsSelected] = useState(false);
  const [unselectedRows, setUnselectedRows] = useState<string[]>([]);

  const numberOfSelectedRows = areAllRowsSelected
    ? totalPayables - unselectedRows.length
    : selectedRows.length;

  useEffect(() => {
    const unselectedIds = payables
      .filter((payable) => !selectedRows.includes(payable.id))
      .map((payable) => payable.id);
    setUnselectedRows(unselectedIds);
    if (unselectedIds.length) {
      setAreAllRowsSelected(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (areAllRowsSelected) {
      const filteredPayables = payables.filter(
        (payable) => !unselectedRows.includes(payable.id),
      );
      setSelectedRows(filteredPayables.map(({ id }) => id));
    }
  }, [payables]);

  if (isLoading && (!payables || payables.length === 0)) {
    return <PayableTableSkeleton />;
  }

  return (
    <>
      <Table<Payable>
        columns={columns}
        data={data}
        getRowId={(payable) => payable.id}
        onRowClick={(payable) => onClick(payable.id)}
        getIsRowActive={({ id }) => id === activePayableId}
        footer={
          hasNextPage && (
            <div className="mx-auto">
              <Button
                variant="secondaryNeutral"
                text={t('payables.loadMore')}
                onClick={onFetchNextPage}
              />
            </div>
          )
        }
        {...(renderActions
          ? {
              selectedRowIds: selectedRows,
              onAllRowsSelectionChange: (_, ids, isChecked) => {
                setAreAllRowsSelected(isChecked);
                return isChecked ? setSelectedRows(ids) : setSelectedRows([]);
              },
              onRowSelectionChange: (_, id, isChecked) => {
                setSelectedRows((state) =>
                  isChecked
                    ? state.concat(id)
                    : state.filter((purchaseOrderId) => purchaseOrderId !== id),
                );
              },
            }
          : {
              selectedRowIds: undefined,
              onAllRowsSelectionChange: undefined,
              onRowSelectionChange: undefined,
            })}
        groupBy={(payable) => {
          const payableDate = payable.creationDate
            ? new Date(payable.creationDate)
            : null;
          return payableDate && isValid(payableDate)
            ? `${payableDate.getUTCFullYear()}-${payableDate.getUTCMonth()}`
            : 'none';
        }}
        renderGroupedRowHeader={(value) => {
          const [year, month] = value
            .split('-')
            .map((part) => Number.parseInt(part));

          return (
            <PayableTableGroup
              stats={{
                year,
                month,
              }}
            />
          );
        }}
      />

      {renderActions?.({
        numberOfSelectedRows,
        areAllRowsSelected,
        unselectedRows,
        selectedRows,
        setSelectedRows,
      })}
    </>
  );
};
